/**
 * components/ui/WarehouseSwitcher.tsx
 * ─────────────────────────────────────
 * Header control for the active warehouse scope.
 *
 * admin / super_admin  → <select> dropdown listing all warehouses plus an
 *                         "All Warehouses" sentinel.
 * other roles          → read-only badge showing the scoped warehouse name
 *                         (falls back to the UUID prefix if the list is empty).
 */

import { useWarehouse } from '@/contexts/WarehouseContext';
import { useAuth }      from '@/contexts/AuthContext';

export function WarehouseSwitcher() {
  const { user } = useAuth();
  const { warehouses, activeWarehouseId, setActiveWarehouseId, isLoading } =
    useWarehouse();

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  if (isAdmin) {
    return (
      <select
        className="wh-switcher-select"
        value={activeWarehouseId ?? ''}
        onChange={e => setActiveWarehouseId(e.target.value || null)}
        disabled={isLoading}
        aria-label="Active warehouse scope"
        title="Switch warehouse scope"
      >
        <option value="">All Warehouses</option>
        {warehouses.map(w => (
          <option key={w.id} value={w.id}>
            {w.name} ({w.code})
          </option>
        ))}
      </select>
    );
  }

  // Non-admin: read-only display
  const label = warehouses.find(w => w.id === activeWarehouseId)?.name
    ?? (activeWarehouseId ? activeWarehouseId.slice(0, 8) + '…' : '—');

  return (
    <span className="wh-switcher-badge" title={activeWarehouseId ?? undefined}>
      🏭 {label}
    </span>
  );
}
